import { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';

import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

export default function RichTextEditor({ value, onChange }: RichTextEditorProps) {
  const [textColor, setTextColor] = useState('#000000');

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({
        placeholder: 'Enter content...',
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const addImage = () => {
    const url = window.prompt('Enter image URL');
    if (url) editor?.chain().focus().setImage({ src: url }).run();
  };

  const setLink = () => {
    const url = window.prompt('Enter link URL');
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  };

  if (!editor) return null;

  const plainText = editor.getText();
  const wordCount = plainText.trim().split(/\s+/).filter(Boolean).length;
  const charCount = plainText.length;

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          p: 1.2,
          borderBottom: '1px solid #ddd',
          backgroundColor: '#fafafa',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Tooltip title="Bold">
          <IconButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'primary' : 'default'}
            size="small"
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Italic">
          <IconButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'primary' : 'default'}
            size="small"
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Underline">
          <IconButton
            onClick={() => editor.chain().focus().toggleUnderline?.().run()}
            color={editor.isActive('underline') ? 'primary' : 'default'}
            size="small"
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Bullet List">
          <IconButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={editor.isActive('bulletList') ? 'primary' : 'default'}
            size="small"
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Number List">
          <IconButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={editor.isActive('orderedList') ? 'primary' : 'default'}
            size="small"
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Color Picker */}
        <Tooltip title="Change Text Color">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small">
              <FormatColorTextIcon sx={{ color: textColor }} fontSize="small" />
            </IconButton>
            <input
              type="color"
              value={textColor}
              onChange={(e) => {
                const color = e.target.value;
                setTextColor(color);
                editor.chain().focus().setColor(color).run();
              }}
              style={{
                width: 24,
                height: 24,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
            />
          </Box>
        </Tooltip>

        <Tooltip title="Insert Link">
          <IconButton size="small" onClick={setLink}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Insert Image">
          <IconButton size="small" onClick={addImage}>
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Editor Content */}
      <Box
        sx={{
          p: 2,
          minHeight: 150,
          position: 'relative',
          '& .ProseMirror': {
            outline: 'none', // removes blue outline/focus ring
            border: 'none', // removes inner border
            boxShadow: 'none',
            fontFamily: 'inherit',
            fontSize: '0.95rem',
          },
        }}
      >
        <EditorContent editor={editor} />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 6,
            right: 10,
            color: 'gray',
          }}
        >
          CHARS: {charCount} WORDS: {wordCount}
        </Typography>
      </Box>
    </Box>
  );
}
